import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { statusColor, parseRange } from "@/lib/health";
import {
  TrendingUp, TrendingDown, Minus, Search, LineChart as LineChartIcon,
  ArrowUpRight, ArrowDownRight, Loader2, Sparkles, Activity, ArrowRight,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceArea, CartesianGrid,
} from "recharts";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "worsening", label: "Worsening", color: "text-danger" },
  { id: "improving", label: "Improving", color: "text-success" },
  { id: "stable", label: "Stable", color: "text-ink/60" },
];

export default function PatientTrends() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("self");   // "self" | "<family_member_id>" | "all"

  const loadTrends = (memberId) => {
    setLoading(true);
    api.get(`/reports/patient/trends?family_member_id=${encodeURIComponent(memberId)}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ reports_count: 0, tracked_biomarkers: 0, biomarkers: [], family_members: [{ id: "self", full_name: "Myself" }] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTrends(scope); }, [scope]);

  const biomarkers = useMemo(() => {
    const list = data?.biomarkers || [];
    return list.filter((b) => {
      if (filter !== "all" && b.trend !== filter) return false;
      if (q && !`${b.name} ${b.category}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, filter, q]);

  const counts = useMemo(() => {
    const c = { worsening: 0, improving: 0, stable: 0 };
    (data?.biomarkers || []).forEach((b) => { c[b.trend] = (c[b.trend] || 0) + 1; });
    return c;
  }, [data]);

  return (
    <DashboardLayout role="patient">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Longitudinal insights</div>
            <h1 className="mt-1 font-heading font-bold text-3xl sm:text-4xl text-ink">Your health trends</h1>
            <p className="mt-1 text-ink/60 max-w-2xl">Every biomarker that appears in two or more of your reports — how it&apos;s changed over time.</p>
          </div>
        </div>

        {/* Family scope filter — Self / each family member / All (households) */}
        {(data?.family_members?.length || 0) > 1 && (
          <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="trends-family-chips">
            {(data.family_members || []).map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`trends-scope-${m.id}`}
                onClick={() => setScope(m.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${scope === m.id ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-border hover:border-ink"}`}
              >
                {m.full_name}{m.relation && m.id !== "self" ? <span className="opacity-60 ml-1">· {m.relation}</span> : null}
              </button>
            ))}
            <button
              type="button"
              data-testid="trends-scope-all"
              onClick={() => setScope("all")}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${scope === "all" ? "bg-efgh-orange text-white border-efgh-orange" : "bg-white text-ink/70 border-border hover:border-ink"}`}
            >
              All household
            </button>
          </div>
        )}

        {loading ? (
          <div className="mt-16 grid place-items-center text-ink/40" data-testid="trends-loading">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : (data?.biomarkers?.length || 0) === 0 ? (
          <EmptyState reportsCount={data?.reports_count || 0} />
        ) : (
          <>
            {/* KPI strip */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={LineChartIcon} color="text-efgh-blue" bg="bg-efgh-blue/10" value={data.tracked_biomarkers} label="Tracked biomarkers" onClick={() => setFilter("all")} testid="kpi-tracked" />
              <KpiCard icon={Activity} color="text-ink" bg="bg-muted" value={data.reports_count} label="Reports analysed" to="/patient/reports" testid="kpi-reports" />
              <KpiCard icon={TrendingDown} color="text-danger" bg="bg-danger-light" value={counts.worsening || 0} label="Worsening" onClick={() => setFilter("worsening")} testid="kpi-worsening" />
              <KpiCard icon={TrendingUp} color="text-success" bg="bg-success-light" value={counts.improving || 0} label="Improving" onClick={() => setFilter("improving")} testid="kpi-improving" />
            </div>

            {/* Filters + search */}
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <div className="flex gap-1.5 flex-wrap" data-testid="trend-filters">
                {FILTERS.map((f) => {
                  const active = filter === f.id;
                  const count = f.id === "all" ? data.tracked_biomarkers : (counts[f.id] || 0);
                  return (
                    <button
                      key={f.id}
                      data-testid={`filter-${f.id}`}
                      onClick={() => setFilter(f.id)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${active ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-border hover:border-ink/40"}`}
                    >
                      {f.label} <span className="ml-1 text-[11px] opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                <Input
                  data-testid="trend-search"
                  placeholder="Search biomarker or category…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9 w-64 h-10"
                />
              </div>
            </div>

            {/* Cards grid */}
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {biomarkers.map((b) => <BiomarkerTrendCard key={b.name} b={b} />)}
              {biomarkers.length === 0 && (
                <Card className="p-10 rounded-2xl text-center col-span-full text-ink/50 text-sm">
                  No biomarkers match your filters.
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

/* ============ EMPTY STATE ============ */
function EmptyState({ reportsCount }) {
  return (
    <Card className="mt-8 p-10 rounded-2xl text-center" data-testid="trends-empty">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-efgh-blue/10 text-efgh-blue grid place-items-center">
        <LineChartIcon className="w-7 h-7" />
      </div>
      <div className="mt-4 font-heading font-semibold text-ink text-xl">
        {reportsCount < 2 ? "Upload another report to unlock trends" : "No overlapping biomarkers yet"}
      </div>
      <p className="mt-2 text-sm text-ink/60 max-w-md mx-auto">
        We compare biomarkers across your reports and highlight what&apos;s improving, worsening, or holding steady. Once you have two or more published reports with common biomarkers, they&apos;ll appear here.
      </p>
      <Link to="/patient/upload">
        <Button data-testid="trends-upload-cta" className="mt-6 h-11 bg-ink hover:bg-ink/90 text-white">
          <Sparkles className="w-4 h-4 mr-2" /> Upload a report
        </Button>
      </Link>
    </Card>
  );
}

/* ============ KPI CARD ============ */
function KpiCard({ icon: Icon, color, bg, value, label, onClick, to, testid }) {
  const clickable = !!(onClick || to);
  const inner = (
    <Card
      className={`p-5 rounded-2xl ${clickable ? "card-hover cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`}
      data-testid={testid}
      onClick={onClick}
    >
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/* ============ TREND CARD ============ */
function BiomarkerTrendCard({ b }) {
  const range = useMemo(() => parseRange(b.reference_range), [b.reference_range]);
  const values = b.series.map((p) => p.value);
  const min = Math.min(...values, range?.min ?? Infinity);
  const max = Math.max(...values, range?.max ?? -Infinity);
  const span = Math.max(max - min, Math.abs(max) * 0.1, 1);
  const yMin = min - span * 0.15;
  const yMax = max + span * 0.15;

  const trendMeta = {
    worsening: { icon: TrendingDown, color: "#E53935", label: "Worsening", chipBg: "bg-danger-light", chipFg: "text-danger" },
    improving: { icon: TrendingUp, color: "#2E7D32", label: "Improving", chipBg: "bg-success-light", chipFg: "text-success" },
    stable: { icon: Minus, color: "#64748b", label: "Stable", chipBg: "bg-muted", chipFg: "text-ink/70" },
  }[b.trend] || { icon: Minus, color: "#64748b", label: "Stable", chipBg: "bg-muted", chipFg: "text-ink/70" };
  const TIcon = trendMeta.icon;
  const DeltaIcon = b.delta > 0 ? ArrowUpRight : b.delta < 0 ? ArrowDownRight : Minus;

  const latestReportId = b.series[b.series.length - 1]?.report_id;

  return (
    <Card className="p-5 rounded-2xl card-hover" data-testid={`trend-card-${b.name.replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-ink truncate">{b.name}</div>
          <div className="text-xs text-ink/50 truncate">{b.category || "—"} · {b.points} readings</div>
        </div>
        <span className={`pill ${trendMeta.chipBg} ${trendMeta.chipFg} shrink-0`}>
          <TIcon className="w-3 h-3" /> {trendMeta.label}
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-3">
        <div className="font-heading font-bold text-2xl text-ink">
          {b.latest_value}
          <span className="text-xs text-ink/50 font-normal ml-1">{b.unit}</span>
        </div>
        <div className={`flex items-center gap-0.5 text-sm font-medium`} style={{ color: trendMeta.color }}>
          <DeltaIcon className="w-4 h-4" />
          {b.delta > 0 ? "+" : ""}{b.delta}{b.unit ? ` ${b.unit}` : ""}
          <span className="ml-1 text-xs opacity-80">({b.delta_pct > 0 ? "+" : ""}{b.delta_pct}%)</span>
        </div>
      </div>
      {b.reference_range && <div className="text-[11px] text-ink/50 mt-1">Normal: {b.reference_range}</div>}

      <div className="mt-3 h-40" data-testid={`trend-chart-${b.name.replace(/\s+/g, "-")}`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={b.series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis domain={[yMin, yMax]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={34} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }} formatter={(v) => [`${v} ${b.unit || ""}`, "Value"]} />
            {range && (
              <ReferenceArea y1={range.min} y2={range.max} strokeOpacity={0} fill="#4CAF50" fillOpacity={0.1} />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={statusColor(b.latest_status)}
              strokeWidth={2.5}
              dot={{ r: 3, strokeWidth: 0, fill: statusColor(b.latest_status) }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {latestReportId && (
        <Link
          to={`/report/${latestReportId}`}
          data-testid={`trend-view-latest-${b.name.replace(/\s+/g, "-")}`}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-ink hover:underline underline-offset-4"
        >
          View latest report <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </Card>
  );
}
